import {
	ListSkeleton,
	SelectField,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from '@atlas/ui-components'
import { Badge, Button } from '@atlas/ui-primitives'
import { useState } from 'react'
import type {
	WorkflowStatusDTO,
	WorkflowTransitionDTO,
	WorkflowTransitionUpdateBody,
} from '@/domain/workflow-admin.types'
import { TextField } from '@/pages/Classification/FormField'
import { EmptyState, ErrorBanner, ErrorState } from './states'

/**
 * A workflow's transitions. Source and destination are picked from the same
 * workflow's statuses — a status code only means anything inside the workflow
 * that defines it, so the pickers are never fed a global status list.
 *
 * Transition legality beyond the endpoints' workflow membership is the
 * backend's call; nothing here re-decides it.
 */

export interface TransitionListProps {
	transitions: WorkflowTransitionDTO[]
	/** The statuses of the workflow being edited — the only options either picker offers. */
	statuses: WorkflowStatusDTO[]
	isLoading?: boolean
	error?: unknown
	onRetry?: () => void
	onSave: (transitionId: string, body: WorkflowTransitionUpdateBody) => void
	onDelete?: (transitionId: string) => void
	pendingTransitionId?: string | null
	saveError?: unknown
}

interface Draft {
	fromStatusId: string
	toStatusId: string
	ctaLabel: string
	position: string
	isActive: boolean
}

const draftOf = (transition: WorkflowTransitionDTO): Draft => ({
	fromStatusId: transition.fromStatusId,
	toStatusId: transition.toStatusId,
	ctaLabel: transition.ctaLabel,
	position: String(transition.position),
	isActive: transition.isActive,
})

const bodyOf = (draft: Draft): WorkflowTransitionUpdateBody => ({
	fromStatusId: draft.fromStatusId,
	toStatusId: draft.toStatusId,
	ctaLabel: draft.ctaLabel.trim(),
	position: Number(draft.position),
	isActive: draft.isActive,
})

function TransitionEditRow({
	transition,
	props,
	labelOf,
	options,
}: {
	transition: WorkflowTransitionDTO
	props: TransitionListProps
	labelOf: (statusId: string) => string
	options: { value: string; label: string }[]
}) {
	const { onSave, onDelete, pendingTransitionId, saveError } = props
	const [draft, setDraft] = useState<Draft>(() => draftOf(transition))
	const [editing, setEditing] = useState(false)
	const isPending = pendingTransitionId === transition.transitionId
	const set =
		<K extends keyof Draft>(key: K) =>
		(value: Draft[K]) =>
			setDraft({ ...draft, [key]: value })

	if (!editing) {
		return (
			<TableRow data-testid={`transition-row-${transition.transitionId}`}>
				<TableCell>{transition.position}</TableCell>
				<TableCell>{labelOf(transition.fromStatusId)}</TableCell>
				<TableCell>{labelOf(transition.toStatusId)}</TableCell>
				<TableCell>{transition.ctaLabel}</TableCell>
				<TableCell>
					{transition.isActive ? (
						<Badge size="sm">active</Badge>
					) : (
						<Badge variant="muted" size="sm">
							inactive
						</Badge>
					)}
				</TableCell>
				<TableCell className="flex gap-2">
					<Button variant="outline" onPress={() => setEditing(true)} aria-label={`Edit ${transition.ctaLabel}`}>
						Edit
					</Button>
					{onDelete && (
						<Button
							variant="outline"
							onPress={() => onDelete(transition.transitionId)}
							aria-label={`Delete ${transition.ctaLabel}`}
						>
							Delete
						</Button>
					)}
				</TableCell>
			</TableRow>
		)
	}

	return (
		<TableRow data-testid={`transition-edit-${transition.transitionId}`}>
			<TableCell colSpan={6}>
				<div className="flex flex-wrap items-end gap-3">
					<SelectField
						isClearable={false}
						label="From"
						value={draft.fromStatusId}
						onChange={set('fromStatusId')}
						options={options}
					/>
					<SelectField
						isClearable={false}
						label="To"
						value={draft.toStatusId}
						onChange={set('toStatusId')}
						options={options}
					/>
					<TextField label="CTA label" value={draft.ctaLabel} onChange={set('ctaLabel')} />
					<TextField label="Position" value={draft.position} onChange={set('position')} />
					<label className="flex items-center gap-2 text-sm">
						<input type="checkbox" checked={draft.isActive} onChange={(e) => set('isActive')(e.target.checked)} />
						Active
					</label>
					<Button
						isDisabled={isPending || !draft.ctaLabel.trim()}
						onPress={() => onSave(transition.transitionId, bodyOf(draft))}
						aria-label={`Save ${transition.ctaLabel}`}
					>
						{isPending ? 'Saving…' : 'Save'}
					</Button>
					<Button
						variant="outline"
						onPress={() => {
							setDraft(draftOf(transition))
							setEditing(false)
						}}
					>
						Cancel
					</Button>
				</div>
				{saveError ? <ErrorBanner error={saveError} title="Could not save transition" /> : null}
			</TableCell>
		</TableRow>
	)
}

export function TransitionList(props: TransitionListProps) {
	const { transitions, statuses, isLoading, error, onRetry } = props
	if (isLoading) return <ListSkeleton label="Loading transitions" />
	if (error) return <ErrorState error={error} onRetry={onRetry} title="Could not load transitions" />
	if (transitions.length === 0) {
		return (
			<EmptyState
				title="No transitions yet"
				description="Statuses in this workflow have no configured moves between them."
			/>
		)
	}
	const options = statuses.map((status) => ({ value: status.statusId, label: `${status.label} (${status.code})` }))
	const labelOf = (statusId: string) => options.find((o) => o.value === statusId)?.label ?? statusId
	const ordered = [...transitions].sort((a, b) => a.position - b.position)
	return (
		<Table>
			<TableHeader>
				<TableRow>
					<TableHead>Position</TableHead>
					<TableHead>From</TableHead>
					<TableHead>To</TableHead>
					<TableHead>CTA</TableHead>
					<TableHead>State</TableHead>
					<TableHead>Actions</TableHead>
				</TableRow>
			</TableHeader>
			<TableBody>
				{ordered.map((transition) => (
					<TransitionEditRow
						key={transition.transitionId}
						transition={transition}
						props={props}
						labelOf={labelOf}
						options={options}
					/>
				))}
			</TableBody>
		</Table>
	)
}
